/**
 * Scans & Entwürfe am Projekt — Polycam-Scans, SketchUp-Dateien, Renderings, Filme, Pläne.
 *
 * Benjamin, 13.10.2026: „Ich möchte nicht in Nextcloud arbeiten — das muss über die
 * Kontor-App laufen." Die Dateien liegen in der Nextcloud im Projektordner, aber
 * hineingelegt und herausgeholt wird hier. Ein Scan geht an die Scan-Werkstatt auf
 * dem Evo (`3D/1-Polycam`) und erscheint danach mit den Dateien für SketchUp und
 * Twinmotion. Die drehbare 3D-Ansicht folgt in einer späteren Etappe.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { fehlerText } from "@vimaana/ui";
import DokumentLightbox, { type Dokument } from "../DokumentLightbox";
import { useMedien, useMedienAblegen, useMediumAendern, type Hochladen, type Medium, type MediumArt } from "../../hooks/useMedien";
import Bestandsmodell from "./ProjektBestandsmodell";
import "../../styles/fotos.css";

/** Was der Server annimmt (`ARTEN` in kontor_medien.py). Kein `accept` am Auswahlfeld: macOS graut
 *  sonst je nach Browser .skp-Dateien aus, die es keinem Dateityp zuordnet (16.10.2026). Geprüft wird hier. */
const ENDUNGEN = ["zip", "glb", "gltf", "skp", "jpg", "jpeg", "png", "webp", "heic", "mp4", "mov", "m4v", "pdf", "dwg", "dxf"];
/** Fertiges Modell zuerst, Scan-Rohdaten zuletzt — Volker lud am 03.11.2026 den Rohscan statt der .skp. */
const GRUPPEN: { art: MediumArt; titel: string }[] = [
  { art: "sketchup", titel: "SketchUp-Modelle" },
  { art: "plan", titel: "Pläne" },
  { art: "rendering", titel: "Renderings" },
  { art: "film", titel: "Filme" },
  { art: "scan", titel: "3D-Scans & Bestandsmodell" },
];
const ZUSTAND: Record<string, string> = { wartet: "wird aufbereitet", fertig: "fertig", fehler: "Fehler" };

function groesse(bytes: number): string {
  if (bytes >= 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024 / 1024).toFixed(1).replace(".", ",")} GB`;
  if (bytes >= 1024 * 1024) return `${Math.round(bytes / 1024 / 1024)} MB`;
  return `${Math.max(1, Math.round(bytes / 1024))} kB`;
}

function datum(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" });
}

export default function ProjektMedien({ projektId }: { projektId: string }) {
  const { data, isLoading, isError, error, refetch, isFetching } = useMedien(projektId);
  const ablegen = useMedienAblegen(projektId);
  const aendern = useMediumAendern(projektId);
  const feld = useRef<HTMLInputElement>(null);
  const [fortschritt, setFortschritt] = useState<Hochladen | null>(null);
  const [ziehen, setZiehen] = useState(false);
  const [abgelehnt, setAbgelehnt] = useState<string[]>([]);
  const [neu, setNeu] = useState<string[]>([]);
  const [entfernen, setEntfernen] = useState<string | null>(null);
  // Renderings in der Dokumentansicht statt in einem neuen Tab (30.10.2026).
  const [dok, setDok] = useState<Dokument>(null);

  const gruppen = useMemo(
    () => GRUPPEN.map((g) => ({ ...g, liste: (data ?? []).filter((m) => m.art === g.art) })).filter((g) => g.liste.length),
    [data],
  );

  function hochladen(auswahl: File[]) {
    if (!auswahl.length || ablegen.isPending) return;
    setNeu([]);
    // Polycam legt beim Entpacken OBJ, MTL und Texturen nebeneinander. Allein ist die OBJ
    // ohne Farbe und ohne Material — gebraucht wird die ganze ZIP (13.10.2026).
    const endung = (f: File) => (f.name.includes(".") ? f.name.split(".").pop()!.toLowerCase() : "");
    const einzeln = auswahl.filter((f) => ["obj", "mtl"].includes(endung(f)));
    const fremd = auswahl.filter((f) => !einzeln.includes(f) && !ENDUNGEN.includes(endung(f)));
    const dateien = auswahl.filter((f) => !einzeln.includes(f) && !fremd.includes(f));
    const hinweis = [
      ...einzeln.map((f) => `${f.name}: bitte die ganze ZIP-Datei aus Polycam wählen (z. B. „Schwimmteich.zip“) — `
        + "darin stecken Modell, Material und die Fotos des Scans."),
      ...fremd.map((f) => `${f.name}: diese Dateiart nimmt Kontor hier nicht an (Scan als ZIP oder GLB, SketchUp, Bild, Film oder PDF).`),
    ];
    setAbgelehnt(hinweis);
    if (!dateien.length) {
      if (feld.current) feld.current.value = "";
      return;
    }
    ablegen.mutate(
      { dateien, fortschritt: setFortschritt },
      {
        onSuccess: (e) => {
          setAbgelehnt([...hinweis, ...e.abgelehnt]);
          setNeu(e.angelegt.map((m) => m.titel || m.dateiname));
        },
        onSettled: () => {
          setFortschritt(null);
          if (feld.current) feld.current.value = "";
        },
      },
    );
  }

  // Ziehen über die ganze Seite: das Feld leuchtet auf, und eine danebengefallene Datei
  // öffnet nicht den Browser (dann wäre Kontor weg).
  const hochladenRef = useRef(hochladen);
  hochladenRef.current = hochladen;
  useEffect(() => {
    let tiefe = 0;
    const mitDateien = (e: DragEvent) => Array.from(e.dataTransfer?.types ?? []).includes("Files");
    const rein = (e: DragEvent) => { if (!mitDateien(e)) return; tiefe += 1; setZiehen(true); };
    const ueber = (e: DragEvent) => { if (mitDateien(e)) e.preventDefault(); };
    const raus = (e: DragEvent) => { if (!mitDateien(e)) return; tiefe = Math.max(0, tiefe - 1); if (!tiefe) setZiehen(false); };
    const fallen = (e: DragEvent) => { if (!mitDateien(e)) return; e.preventDefault(); tiefe = 0; setZiehen(false); };
    window.addEventListener("dragenter", rein);
    window.addEventListener("dragover", ueber);
    window.addEventListener("dragleave", raus);
    window.addEventListener("drop", fallen);
    return () => {
      window.removeEventListener("dragenter", rein);
      window.removeEventListener("dragover", ueber);
      window.removeEventListener("dragleave", raus);
      window.removeEventListener("drop", fallen);
    };
  }, []);

  const mb = (b: number) => (b >= 1024 * 1024 ? `${(b / 1024 / 1024).toFixed(1).replace(".", ",")} MB` : `${Math.max(1, Math.round(b / 1024))} kB`);
  const prozent = fortschritt && fortschritt.groesse ? Math.round((fortschritt.geladen / fortschritt.groesse) * 100) : 0;

  return (
    <div className="vg-unterblock">
      <div className="vg-doku-kopf">
        <span className="vg-detail-section-title">3D-Scans &amp; Entwürfe</span>
      </div>
      <div
        className={`fo-ablage me-ablage${ziehen ? " is-ziehen" : ""}`}
        role="button"
        tabIndex={0}
        aria-disabled={ablegen.isPending}
        onClick={() => { if (!ablegen.isPending) feld.current?.click(); }}
        onKeyDown={(e) => { if ((e.key === "Enter" || e.key === " ") && !ablegen.isPending) { e.preventDefault(); feld.current?.click(); } }}
        onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = ablegen.isPending ? "none" : "copy"; }}
        onDrop={(e) => { e.preventDefault(); e.stopPropagation(); setZiehen(false); hochladen(Array.from(e.dataTransfer.files)); }}
      >
        <svg className="fo-ablage-icon" viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="currentColor"
          strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M12 16V4M7 9l5-5 5 5" />
          <path d="M4 15v4a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-4" />
        </svg>
        <strong>{ablegen.isPending ? "Wird hochgeladen …" : ziehen ? "Hier loslassen — das Hochladen beginnt" : "SketchUp-Datei, Scan oder Plan hierher ziehen"}</strong>
        <span>oder klicken, um Dateien auszuwählen</span>
      </div>
      <input
        ref={feld}
        type="file"
        multiple
        className="fo-unsichtbar"
        tabIndex={-1}
        aria-hidden="true"
        onChange={(e) => hochladen(Array.from(e.target.files ?? []))}
      />
      {fortschritt && (
        <div className="fo-fortschritt fo-arbeit" role="status" aria-live="polite">
          <div className="fo-fortschritt-kopf">
            <strong>
              {fortschritt.gesamt > 1 ? `Datei ${fortschritt.nr} von ${fortschritt.gesamt}: ` : ""}{fortschritt.name}
            </strong>
            <span>
              {fortschritt.groesse && fortschritt.geladen >= fortschritt.groesse
                ? "Hochgeladen — wird in der Nextcloud abgelegt …"
                : `${mb(fortschritt.geladen)} von ${mb(fortschritt.groesse)} · ${prozent} %`}
            </span>
          </div>
          <div className={`fo-balken${fortschritt.geladen >= fortschritt.groesse ? " is-laeuft" : ""}`} aria-hidden="true">
            <i style={{ width: `${Math.max(3, prozent)}%` }} />
          </div>
        </div>
      )}
      <small className="vg-doku-hinweis">
        Polycam-Scan als ganze ZIP-Datei (OBJ) oder GLB, SketchUp-Dateien, Renderings, Filme, PDF-Pläne. In Polycam:
        Exportieren → OBJ → „In Dateien sichern“, dann die ZIP hier hinzufügen.
      </small>

      {abgelehnt.length > 0 && (
        <div className="vg-banner is-warn">
          {abgelehnt.length === 1 ? "Eine Datei wurde nicht gespeichert:" : `${abgelehnt.length} Dateien wurden nicht gespeichert:`}
          <ul>{abgelehnt.map((g) => <li key={g}>{g}</li>)}</ul>
        </div>
      )}
      {neu.length > 0 && (
        <div className="vg-banner" role="status">
          {neu.length === 1 ? `„${neu[0]}“ ist hinzugefügt.` : `${neu.length} Dateien sind hinzugefügt.`}
          {" "}Ein Scan wird jetzt von der Scan-Werkstatt aufbereitet (alle 10 Minuten, 6–22 Uhr).
        </div>
      )}
      {ablegen.isError && <div className="vg-banner is-warn">{fehlerText(ablegen.error)}</div>}
      {isLoading && <div className="vg-detail-section-hint">Wird geladen …</div>}
      {isError && (
        <div className="vg-detail-section-hint">
          {fehlerText(error, "Die Dateien ließen sich nicht laden.")}{" "}
          <button type="button" className="vg-btn" disabled={isFetching} onClick={() => refetch()}>
            {isFetching ? "Lädt …" : "Neu laden"}
          </button>
        </div>
      )}
      {!isLoading && !isError && !gruppen.length && (
        <div className="vg-detail-section-hint">Noch kein Scan und kein Entwurf zu diesem Projekt.</div>
      )}

      {gruppen.map((g) => (
        <div className="me-gruppe" key={g.art}>
          <div className="me-gruppe-titel">{g.titel} <span className="vg-proj-tab-count">{g.liste.length}</span></div>
          {g.art === "rendering" ? (
            <div className="me-bilder">
              {g.liste.map((m) => (
                <button
                  type="button"
                  className="me-bild"
                  key={m.id}
                  onClick={() => setDok({ titel: m.titel || m.dateiname || "Rendering", unterzeile: datum(m.created_at), url: m.url, bild: true })}
                >
                  <img src={m.url} alt={m.titel || "Rendering"} loading="lazy" />
                  <span>{m.titel || m.dateiname} · {datum(m.created_at)}</span>
                </button>
              ))}
            </div>
          ) : (
            g.liste.map((m, i) => (
              <Zeile
                key={m.id}
                m={m}
                erste={i === 0}
                mehrere={g.liste.length > 1}
                projektId={projektId}
                entfernen={entfernen === m.id}
                onEntfernen={() => (entfernen === m.id ? aendern.mutate({ id: m.id, verwerfen: true }) : setEntfernen(m.id))}
              />
            ))
          )}
        </div>
      ))}
      {aendern.isError && <div className="vg-banner is-warn">{fehlerText(aendern.error)}</div>}
      <DokumentLightbox dok={dok} onClose={() => setDok(null)} />
    </div>
  );
}

/** Rohdaten eines Scans mit Dateiart — damit niemand eine ZIP für ein Modell hält. */
const ROHDATEN: Record<string, string> = { sketchup: "Rohscan (.dae, über Importieren)", twinmotion: "Für Twinmotion (.glb)", steckbrief: "Steckbrief" };

function Zeile({ m, projektId, erste, mehrere, entfernen, onEntfernen }: { m: Medium; projektId: string; erste: boolean; mehrere: boolean; entfernen: boolean; onEntfernen: () => void }) {
  const scan = m.art === "scan";
  const modell = m.art === "sketchup";
  const aktuell = modell && erste; // die Liste kommt neueste zuerst
  const [rohOffen, setRohOffen] = useState(false);
  const bauset = m.modell?.dateien.find((d) => d.teil === "bauset");
  const weg = <button type="button" className="vg-btn" onClick={onEntfernen}>{entfernen ? "Wirklich entfernen?" : "Entfernen"}</button>;
  const rohdaten = (
    <>
      {m.dateien.map((d) => (
        <a key={d.teil} className="vg-btn" href={d.url} target={d.teil === "steckbrief" ? "_blank" : undefined} rel="noreferrer">
          {ROHDATEN[d.teil] ?? d.name}
        </a>
      ))}
      <a className="vg-btn" href={m.url}>Scan-Original (ZIP)</a>
      {bauset && <a className="vg-btn" href={bauset.url}>Bauset (nur für die Erweiterung)</a>}
      {weg}
    </>
  );
  return (
    <div className={`me-zeile${aktuell ? " is-modell" : ""}`}>
      <div className="me-name">
        <strong>
          {m.titel || m.dateiname}{" "}
          {aktuell && mehrere && <span className="me-zustand is-fertig">neueste Fassung</span>}
          {!scan && m.zustand && <span className={`me-zustand is-${m.zustand}`}>{ZUSTAND[m.zustand]}</span>}
        </strong>
        <small>
          {groesse(m.groesse)} · {datum(m.created_at)}{m.erstellt_von ? ` · ${m.erstellt_von}` : ""}
        </small>
        {modell && <small>Herunterladen, dann in SketchUp über Datei → Öffnen. Braucht SketchUp 2021 oder neuer.</small>}
        {!scan && m.hinweis && <small>{m.hinweis}</small>}
      </div>
      <div className="me-links">
        {scan ? (
          <button type="button" className="vg-btn" aria-expanded={rohOffen} onClick={() => setRohOffen(!rohOffen)}>
            Rohdaten {rohOffen ? "▴" : "▾"}
          </button>
        ) : (
          <>
            <a className={`vg-btn${aktuell ? " vg-btn-primary" : ""}`} href={m.url}>
              {modell ? "Herunterladen (.skp)" : "Herunterladen"}
            </a>
            {weg}
          </>
        )}
      </div>
      {scan && rohOffen && (
        <div className="me-links bm-dateien">
          <small className="me-roh-hinweis">
            Rohdaten des Handy-Scans — kein fertiges Modell. Das SketchUp-Modell steht oben unter „SketchUp-Modelle“.
          </small>
          {rohdaten}
        </div>
      )}
      {scan && m.modell && <Bestandsmodell m={m} modell={m.modell} projektId={projektId} />}
    </div>
  );
}
